/**
 * ADVANCE-VARSELET LANDER I ADVANCEN (sak 215.9).
 *
 * Varselet peker på forespørselen, fordi `object_type` ikke har noen advance-verdi og programposten
 * ofte ikke finnes når advancen startes (§4.1). Hvor advancen bor, vet man derfor først når man
 * spør — så denne slår den opp for nettopp de radene, og faller tilbake på forespørselen så lenge
 * svaret ikke finnes eller ikke har noen programpost ennå.
 *
 * Alternativet var å skrive adressen inn i varselet da det ble laget. Det holder ikke: den som
 * åpner advancen kan ikke lese motpartens programpost, årgang eller artist, og en adresse som var
 * riktig den dagen programposten manglet, ville vært feil for alltid.
 */
import type { NotificationDto, OrganizationKind, SharedAdvanceDto } from '@tvitvi/shared';
import { useQuery } from '@tanstack/react-query';

import { useActiveOrganizationId } from '@/auth/active-org';
import {
  advanceForEnquiryQueryOptions,
  enquiryQueryOptions,
} from '@/features/agency/agency-queries';

import { notificationRoute, type NotificationRoute } from './notification-route';

const ADVANCE_OPENED = 'shared_advance.opened';

/** Advancens egen adresse for huset som leser, eller null når den ikke kan bygges ennå. */
export function advanceRoute(
  advance: SharedAdvanceDto | null | undefined,
  kind: OrganizationKind,
  bookingArtistId: string | null | undefined,
): NotificationRoute | null {
  if (advance == null) return null;
  if (kind === 'agency') {
    if (bookingArtistId == null) return null;
    return {
      to: '/artists/$artistId/advance/$advanceId',
      params: { artistId: bookingArtistId, advanceId: advance.id },
    };
  }
  if (
    advance.performanceId === null ||
    advance.performanceEditionId === null ||
    advance.performanceFestivalId === null
  ) {
    return null;
  }
  return {
    to: '/festivals/$festivalId/editions/$editionId/performances/$performanceId',
    params: {
      festivalId: advance.performanceFestivalId,
      editionId: advance.performanceEditionId,
      performanceId: advance.performanceId,
    },
    search: { tab: 'advance' },
  };
}

/** `notificationRoute`, men med advance-varselet slått opp til advancen selv. */
export function useNotificationRoute(
  row: Pick<NotificationDto, 'type' | 'objectType' | 'objectId'> &
    Partial<Pick<NotificationDto, 'i18nParams'>>,
  kind: OrganizationKind,
): NotificationRoute | null {
  const organizationId = useActiveOrganizationId() ?? '';
  const enquiryId =
    row.type === ADVANCE_OPENED && row.objectType === 'booking_enquiry' ? row.objectId : null;
  const enabled = enquiryId !== null && organizationId !== '';

  const advance = useQuery({
    ...advanceForEnquiryQueryOptions(organizationId, enquiryId ?? ''),
    enabled,
  });
  /* Byråets adresse går gjennom artisten; forespørselen er byråets egen rad og sier hvem. */
  const enquiry = useQuery({
    ...enquiryQueryOptions(organizationId, enquiryId ?? ''),
    enabled: enabled && kind === 'agency',
  });

  const fallback = notificationRoute(row, kind);
  if (!enabled) return fallback;
  return advanceRoute(advance.data, kind, enquiry.data?.bookingArtistId) ?? fallback;
}
